import { EditableInfoPage, SubmitProps } from '@/components/editable-info-page';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  getAnnouncements,
  getCoachAvailabilities,
  getMeCoach,
  updateCoachAvailabilities,
  updateMeCoach,
} from '@/lib/api-calls';
import {
  coachAvailabilityFormSchema,
  CoachAvailabilityFormState,
  groupAvailabilitiesByDay,
} from '@/lib/coach-availability-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { FrequentTimeZones } from '@airreading/common';
import {
  CoachAvailabilityType,
  SortedDaysOfWeek,
  TeachingPreference,
  UpdateCoachAvailabilitiesOptions,
} from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { WeeklyHoursFormRow } from './weekly-hours-form-row';
import ScheduleMidnightImage from '../assets/schedule-midnight.png';
import { TimeZonePill } from '@/components/time-zone-pill';
import { findTimeZoneByValue } from '@/lib/time-zones';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getRouteApi } from '@tanstack/react-router';
import { Toggle } from '@/components/ui/toggle';

const myAvailabilityRoute = getRouteApi('/_layout/my-availability/');

export function WeeklyHoursList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { schedule } = myAvailabilityRoute.useSearch();
  const availabilityType =
    schedule === CoachAvailabilityType.substitute ? CoachAvailabilityType.substitute : CoachAvailabilityType.regular;

  const [formKey, setFormKey] = useState(0);
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const { data: coachAvailabilities } = useSuspenseQuery(getCoachAvailabilities({ type: availabilityType }));
  const displayAndSelectionTimeZone = profile.timeZone || Temporal.Now.timeZoneId() || FrequentTimeZones.UsCentral;
  const { standardAbbr } = findTimeZoneByValue(displayAndSelectionTimeZone) || {};

  const [pastMidnightSlot, setPastMidnightSlot] = useState<{ dayId: number; index: number } | null>(null);

  const { mutateAsync } = useMutation({
    mutationFn: (input: { body: UpdateCoachAvailabilitiesOptions; availabilityType: CoachAvailabilityType }) =>
      updateCoachAvailabilities(input.body, input.availabilityType),
  });
  const onSubmit = async ({ formData, disableEditing }: SubmitProps<CoachAvailabilityFormState>) => {
    try {
      const data: UpdateCoachAvailabilitiesOptions = formData.availabilitiesByDay.flatMap(({ day, availabilities }) => {
        return availabilities.map((time) => ({
          day,
          startPlainTime: time.startPlainTime,
          endPlainTime: time.endPlainTime,
          timeZone: displayAndSelectionTimeZone,
          type: availabilityType,
        }));
      });

      // Update teaching preferences
      const currentPreferences = [...profile.teachingPreferences].sort();
      const sortedNewPreferences = [...formData.teachingPreferences].sort();
      const preferencesChanged =
        currentPreferences.length !== sortedNewPreferences.length ||
        currentPreferences.some((pref, idx) => pref !== sortedNewPreferences[idx]);

      if (preferencesChanged) {
        await updateMeCoach({
          ...profile,
          timeZone: displayAndSelectionTimeZone,
          currentTimeZone: displayAndSelectionTimeZone,
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          displayName: profile.displayName ?? '',
          secondaryEmail: profile.secondaryEmail ?? '',
          phone: profile.phone ?? '',
          teachingPreferences: formData.teachingPreferences,
        });
        await queryClient.invalidateQueries({ queryKey: getMeCoach().queryKey });
      }

      await mutateAsync({ body: data, availabilityType });
      await queryClient.invalidateQueries({ queryKey: ['COACH_AVAILABILITIES'] });
      await queryClient.invalidateQueries(getAnnouncements());

      toast({
        description: 'Teacher availability updated successfully',
      });

      setFormKey((k) => k + 1);
      disableEditing();
    } catch (e: unknown) {
      toast({
        description:
          airServerErrorFrom(e)?.userFacingMessage ??
          (e instanceof Error
            ? e.message
            : 'Unable to update teacher availability. Please try again or contact Air Reading support.'),
      });
    }
  };

  const defaultValues: CoachAvailabilityFormState = {
    ...groupAvailabilitiesByDay(coachAvailabilities, displayAndSelectionTimeZone),
    teachingPreferences: profile.teachingPreferences,
  };

  const handlePastMidnightClick = (dayId: number, index: number) => {
    const slot = { dayId, index };
    setPastMidnightSlot(slot);
  };

  useEffect(() => {
    if (pastMidnightSlot) return;
    const timer = setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 100);
    return () => clearTimeout(timer);
  }, [pastMidnightSlot]);

  const [substituteTeachingAvailability, setSubstituteTeachingAvailability] = useState(
    () => profile.teachingPreferences?.includes(TeachingPreference.substitute_teaching) ?? false,
  );
  const prevSubstituteRef = useRef(substituteTeachingAvailability);

  // Update teaching preferences when substituteTeachingAvailability changes
  useEffect(() => {
    const prev = prevSubstituteRef.current;
    prevSubstituteRef.current = substituteTeachingAvailability;

    if (prev === substituteTeachingAvailability) return;

    const newPreferences: TeachingPreference[] = substituteTeachingAvailability
      ? [TeachingPreference.regular_schedule, TeachingPreference.substitute_teaching]
      : [TeachingPreference.regular_schedule];

    updateMeCoach({
      ...profile,
      timeZone: displayAndSelectionTimeZone,
      currentTimeZone: displayAndSelectionTimeZone,
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      displayName: profile.displayName ?? '',
      secondaryEmail: profile.secondaryEmail ?? '',
      phone: profile.phone ?? '',
      teachingPreferences: [...newPreferences],
    })
      .then(() => queryClient.invalidateQueries({ queryKey: getMeCoach().queryKey }))
      .catch((e: unknown) => {
        toast({
          description:
            airServerErrorFrom(e)?.userFacingMessage ??
            'Unable to update teaching preferences. Please try again or contact Air Reading support.',
        });
        setSubstituteTeachingAvailability(prev);
      });
  }, [substituteTeachingAvailability]);

  const substituteWeeklyHoursReadOnly =
    schedule === CoachAvailabilityType.substitute && !substituteTeachingAvailability;

  return (
    <>
      <Card className="mb-5 shadow-md p-2">
        <EditableInfoPage
          key={`${formKey}-${schedule}`}
          disabled={substituteWeeklyHoursReadOnly}
          headerText={
            <>
              <p className="font-bold text-2xl tracking-tight align-middle ml-2">
                {schedule === CoachAvailabilityType.regular ? 'Regular hours' : 'Substitute hours'}
              </p>
              <p className="font-normal text-base tracking-tight align-middle ml-2 opacity-80">
                (Enter your teaching availability. All times will be saved in your local timezone.)
              </p>
            </>
          }
          formSchema={coachAvailabilityFormSchema}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          showRedDot={false}
          data-testid="my-availability-weekly-hours-header"
        >
          {({ form, editing }) => {
            return (
              <>
                <div className="mb-2" data-testid="my-availability-weekly-hours-teaching-preference">
                  <div className="flex items-center gap-2">
                    <TimeZonePill
                      timeZone={displayAndSelectionTimeZone}
                      dataTestId="my-availability-weekly-hours-timezone"
                    />
                    {schedule === CoachAvailabilityType.substitute && (
                      <>
                        <p className="font-normal text-base leading-6 tracking-tight align-middle">
                          Substitute teaching availability
                        </p>
                        <Toggle
                          checked={substituteTeachingAvailability}
                          onCheckedChange={setSubstituteTeachingAvailability}
                        />
                      </>
                    )}
                  </div>
                  {editing && (
                    <div className="bg-[#F3F4F6] rounded-md text-[#64748B] p-2 my-4">
                      {schedule === CoachAvailabilityType.substitute ? (
                        <>
                          <p className="font-bold text-base leading-6 tracking-tight align-middle">
                            Substitute teaching
                          </p>
                          <p className="font-normal text-base leading-6 tracking-tight align-middle">
                            By opting into substitute teaching, you may be assigned sessions up to 12 hours before the
                            start time. You will receive an email notification when you're assigned a shift.
                            <br />
                            <br />
                            <span>
                              <AlertTriangle
                                className="inline-block size-5 shrink-0 align-middle mr-1.5 text-[#64748B]"
                                aria-hidden
                                strokeWidth={2}
                              />
                              <b>Important weekly step: </b>
                              Every Thursday after 6:00 PM CT, please log in to review your substitute hours and turn
                              your availability back ON for the following week. You must do this weekly to continue
                              receiving assignments.
                            </span>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-bold text-base leading-6 tracking-tight align-middle">Regular schedule</p>
                          <p className="font-normal text-base leading-6 tracking-tight align-middle">
                            Set your weekly availability for new student assignments and diagnostic assessments. Please
                            only select time slots where you are consistently available for the next 2–3 months. <br />
                            &nbsp;&nbsp;• Requirements: Classes are 30–40 minutes long, occur at the same time each day,
                            and require at least 3 days of availability per week. <br />
                            &nbsp;&nbsp;• Existing Groups: Updating your schedule will not affect your current classes.
                            To request a change for an existing group,
                            <a
                              href="https://help.airreading.com/support/tickets/new"
                              target="_blank"
                              className="text-[#2563EB]"
                            >
                              {' '}
                              please submit a support ticket
                            </a>
                            .
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {SortedDaysOfWeek.map((day, dayId) => {
                    return (
                      <WeeklyHoursFormRow
                        key={day}
                        day={day}
                        dayId={dayId}
                        form={form}
                        editing={editing}
                        timeZoneAbbr={standardAbbr}
                        onPastMidnightClick={handlePastMidnightClick}
                      />
                    );
                  })}
                </div>
              </>
            );
          }}
        </EditableInfoPage>
      </Card>
      <Dialog open={!!pastMidnightSlot} onOpenChange={(open) => !open && setPastMidnightSlot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl text-center">How to open a time slot that crosses midnight</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-[#000000]">
            Time slots cannot extend across two calendar days, so they MUST be opened separately for each day. <br />
            For example, Monday 11:30 pm – 2:00 am, please split it into two separate slots:
          </DialogDescription>
          <img src={ScheduleMidnightImage} alt="Schedule Midnight" />
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="destructive">
                OK
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
